import React from "react";
import { Phone, ClipboardList, Ruler, Wrench, PaintRoller, CheckCircle, Cog, Droplets, Sparkles, Layers, ShieldCheck } from "lucide-react";
import Seo from "../components/site/Seo";
import Breadcrumbs from "../components/site/Breadcrumbs";
import PageHero from "../components/site/PageHero";
import Reveal from "../components/site/Reveal";
import FinalCTA from "../components/site/FinalCTA";
import { IMG } from "../lib/site";

const STEPS = [
  { icon: Phone, title: "Call", desc: "Reach out to Tyler directly. No call centers, no runaround — just a friendly conversation about your project." },
  { icon: ClipboardList, title: "Free Estimate", desc: "Tyler visits your property, inspects the concrete and gives you an honest, written quote with no pressure." },
  { icon: Ruler, title: "Project Planning", desc: "We help you choose the right system and color, then schedule a day that works for you." },
  { icon: Wrench, title: "Surface Preparation", desc: "We diamond-grind the slab, repair cracks and clean every pore. This is the step that makes a floor last." },
  { icon: PaintRoller, title: "Professional Installation", desc: "We apply premium base coats, hand-broadcast your flake and seal it with a clear polyaspartic topcoat." },
  { icon: CheckCircle, title: "Final Walkthrough", desc: "We walk the finished floor with you, answer questions and make sure you're thrilled before we leave." },
];

const COATING_STEPS = [
  { icon: Cog, title: "Grind", desc: "We diamond-grind the concrete using heavy machinery, plus hand grinders for the edges, to ensure optimal adhesion. Dustless vacuums keep the whole process clean." },
  { icon: Wrench, title: "Repair", desc: "After vacuuming and blowing out the floor, we patch every imperfection with a flexible two-part epoxy patch." },
  { icon: Droplets, title: "Coat", desc: "We apply a self-priming, pre-pigmented polyaspartic base coat that is UV-stable." },
  { icon: Sparkles, title: "Flake", desc: "We hand-flake the floor, broadcasting decorative chips across the fresh base coat." },
  { icon: Layers, title: "Scrape", desc: "We scrape the floor, edges, joints and stem walls to remove any excess flakes for a clean, even finish." },
  { icon: ShieldCheck, title: "Seal", desc: "We apply a slow-cure polyaspartic clear coat for protection, shine and optional non-slip texture." },
];

const COATING_INTRO =
  "PRYME EPOXY uses a six-step process for applying polyaspartic coatings to concrete floors — including garage floors, patios, sidewalks, driveways and pool decks.";

const howToSchema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "PRYME EPOXY Polyaspartic Floor Coating Process",
  description: COATING_INTRO,
  step: COATING_STEPS.map((s, i) => ({
    "@type": "HowToStep",
    position: i + 1,
    name: s.title,
    text: s.desc,
  })),
};

const Process = () => (
  <div>
    <Seo
      title="Our Process | How We Install Your Floor | PRYME EPOXY"
      description="See exactly how PRYME EPOXY installs your floor — a six-step polyaspartic process: grind, repair, coat, flake, scrape and seal. Simple, transparent, never cutting corners."
      path="/process"
      jsonLd={howToSchema}
      jsonLdId="process-howto-schema"
    />
    <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Process", path: "/process" }]} />

    <PageHero
      overline="Our Process"
      title="Exactly What Happens, Step by Step"
      subtitle="No surprises. Here's our simple, honest process from your first call to the day you park on your beautiful new floor."
      image={IMG.processGrind}
    />

    <section className="py-16 md:py-20 bg-[#0A0A0A]">
      <div className="max-w-5xl mx-auto px-5 lg:px-12">
        <div className="space-y-5">
          {STEPS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.05}>
              <div className="flex items-start gap-5 border border-zinc-800 bg-[#141414] p-6 md:p-7">
                <div className="shrink-0 flex flex-col items-center">
                  <div className="w-14 h-14 bg-[#C69453]/10 border border-[#C69453]/30 flex items-center justify-center text-[#C69453]">
                    <s.icon className="w-7 h-7" />
                  </div>
                  <span className="text-[#C69453] font-heading text-2xl font-bold mt-3">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <div>
                  <h3 className="text-xl md:text-2xl font-semibold text-white">{s.title}</h3>
                  <p className="text-zinc-400 text-base md:text-lg mt-2 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="py-16 md:py-20 bg-zinc-950 border-y border-zinc-900">
      <div className="max-w-6xl mx-auto px-5 lg:px-12">
        <div className="max-w-3xl mb-12">
          <span className="text-[#C69453] text-sm font-semibold tracking-[0.2em] uppercase">The Coating System</span>
          <h2 className="text-3xl md:text-4xl font-light text-white mt-2 leading-tight">Our Six-Step Polyaspartic Process</h2>
          <p className="text-zinc-400 text-base md:text-lg mt-4 leading-relaxed">{COATING_INTRO}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {COATING_STEPS.map((s, i) => (
            <Reveal key={s.title} delay={(i % 3) * 0.06}>
              <div className="border border-zinc-800 bg-[#141414] p-7 h-full relative">
                <div className="absolute top-0 left-0 w-full h-1 bg-[#C69453]" />
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 bg-[#C69453]/10 border border-[#C69453]/30 flex items-center justify-center text-[#C69453]">
                    <s.icon className="w-6 h-6" />
                  </div>
                  <span className="text-[#C69453] font-heading text-3xl font-bold">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="text-xl font-semibold text-white mt-5">{s.title}</h3>
                <p className="text-zinc-400 text-base mt-2 leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-8 border-l-2 border-[#C69453] pl-5">
          <p className="text-zinc-300 text-base md:text-lg leading-relaxed">
            <span className="text-white font-semibold">Grind &amp; Seal services:</span> we perform a thorough
            double diamond grind to fully prepare the surface before sealing.
          </p>
        </div>
      </div>
    </section>

    <FinalCTA heading="Ready to Get Started? Step One Is a Phone Call." />
  </div>
);

export default Process;
